/**
 * Authorization Code + PKCE flow against ZITADEL.
 *
 * Three entry points:
 *   - `startLogin()`        kicks off the redirect to ZITADEL.
 *   - `handleCallback()`    runs on /auth/callback, exchanges code → tokens.
 *   - `logout()`            clears local state and redirects to ZITADEL end_session.
 *
 * No client library — the spec is small and a hand-roll keeps the
 * dep tree honest. Tests live in `__tests__/oidc.test.ts`.
 */

import {
  deriveCodeChallenge,
  generateCodeVerifier,
  generateState,
} from "./pkce";
import { preRedirectStorage, tokenStorage } from "./storage";

export interface AuthConfig {
  /** OIDC issuer (e.g. `http://127.0.0.1:8092`). */
  issuer: string;
  /** Client id registered as `z01-web`. */
  client_id: string;
  /** Must match the redirect URI registered in ZITADEL. */
  redirect_uri: string;
  /** Space-separated scopes. */
  scope?: string;
}

const DEFAULT_SCOPE = "openid profile email offline_access";

export function authConfigFromEnv(): AuthConfig {
  return {
    issuer: import.meta.env.VITE_OIDC_ISSUER ?? "http://127.0.0.1:8092",
    client_id: import.meta.env.VITE_OIDC_CLIENT_ID ?? "z01-web",
    redirect_uri:
      import.meta.env.VITE_OIDC_REDIRECT_URI ??
      `${window.location.origin}/auth/callback`,
    scope: import.meta.env.VITE_OIDC_SCOPE ?? DEFAULT_SCOPE,
  };
}

export async function startLogin(cfg: AuthConfig): Promise<void> {
  const verifier = generateCodeVerifier();
  const challenge = await deriveCodeChallenge(verifier);
  const state = generateState();

  preRedirectStorage.save({
    code_verifier: verifier,
    state,
    return_to: window.location.pathname + window.location.search,
  });

  const url = new URL(`${trimSlash(cfg.issuer)}/oauth/v2/authorize`);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("client_id", cfg.client_id);
  url.searchParams.set("redirect_uri", cfg.redirect_uri);
  url.searchParams.set("scope", cfg.scope ?? DEFAULT_SCOPE);
  url.searchParams.set("state", state);
  url.searchParams.set("code_challenge", challenge);
  url.searchParams.set("code_challenge_method", "S256");

  window.location.assign(url.toString());
}

export interface CallbackResult {
  return_to: string;
}

export async function handleCallback(
  cfg: AuthConfig,
  search: string = window.location.search,
): Promise<CallbackResult> {
  const params = new URLSearchParams(search);
  const code = params.get("code");
  const returnedState = params.get("state");
  const error = params.get("error");
  const errorDescription = params.get("error_description");

  if (error) {
    preRedirectStorage.clear();
    throw new Error(`oidc error: ${error}: ${errorDescription ?? ""}`);
  }
  if (!code || !returnedState) {
    throw new Error("missing code or state in callback URL");
  }

  const pre = preRedirectStorage.load();
  if (!pre) {
    throw new Error(
      "missing pre-redirect state — probably opened the callback URL directly",
    );
  }
  if (pre.state !== returnedState) {
    preRedirectStorage.clear();
    throw new Error("state mismatch — possible CSRF, refusing to exchange");
  }

  const body = new URLSearchParams();
  body.set("grant_type", "authorization_code");
  body.set("code", code);
  body.set("client_id", cfg.client_id);
  body.set("redirect_uri", cfg.redirect_uri);
  body.set("code_verifier", pre.code_verifier);

  const res = await fetch(`${trimSlash(cfg.issuer)}/oauth/v2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!res.ok) {
    preRedirectStorage.clear();
    throw new Error(`token exchange failed: HTTP ${res.status}`);
  }
  const json = (await res.json()) as {
    access_token: string;
    refresh_token?: string;
    id_token?: string;
    expires_in: number;
  };

  tokenStorage.save({
    access_token: json.access_token,
    refresh_token: json.refresh_token,
    id_token: json.id_token,
    expires_at: Date.now() + json.expires_in * 1000,
  });

  const returnTo = normalizeReturnTo(pre.return_to);
  preRedirectStorage.clear();
  return { return_to: returnTo };
}

export async function refreshTokens(cfg: AuthConfig): Promise<boolean> {
  const tokens = tokenStorage.load();
  if (!tokens?.refresh_token) return false;

  const body = new URLSearchParams();
  body.set("grant_type", "refresh_token");
  body.set("refresh_token", tokens.refresh_token);
  body.set("client_id", cfg.client_id);

  const res = await fetch(`${trimSlash(cfg.issuer)}/oauth/v2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!res.ok) {
    tokenStorage.clear();
    return false;
  }
  const json = (await res.json()) as {
    access_token: string;
    refresh_token?: string;
    id_token?: string;
    expires_in: number;
  };
  tokenStorage.save({
    access_token: json.access_token,
    refresh_token: json.refresh_token ?? tokens.refresh_token,
    id_token: json.id_token ?? tokens.id_token,
    expires_at: Date.now() + json.expires_in * 1000,
  });
  return true;
}

export function logout(cfg: AuthConfig): void {
  const tokens = tokenStorage.load();
  tokenStorage.clear();
  preRedirectStorage.clear();
  const idToken = tokens?.id_token;
  if (idToken) {
    const url = new URL(`${trimSlash(cfg.issuer)}/oidc/v1/end_session`);
    url.searchParams.set("id_token_hint", idToken);
    url.searchParams.set(
      "post_logout_redirect_uri",
      `${window.location.origin}/`,
    );
    window.location.assign(url.toString());
  } else {
    window.location.assign("/");
  }
}

function normalizeReturnTo(returnTo: string): string {
  // Logged-in root lands on the chat surface per CTO direction.
  // Dashboard is reachable via the wordmark menu.
  if (returnTo === "/" || returnTo === "") return "/chat";
  return returnTo;
}

function trimSlash(s: string): string {
  return s.replace(/\/+$/, "");
}
